import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useMeta } from "../lib/meta";
import { formatDate, formatINR, formatPoints } from "../lib/format";
import { describeMultiplier } from "../lib/rules";
import type { Card, MyShare, ReceivedShare, TemplateRuleSpec, TemplateSpec } from "../lib/types";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner } from "../components/ui";
import { ShareModal } from "../components/ShareModal";

/** "10X incl. base on Hotels at smartbuy · cap 15,000 pts a month, bonus only". */
function describeRule(rule: TemplateRuleSpec, labelOf: (key: string) => string): string {
  if (rule.isExclusion) return `Earns nothing on ${rule.categories.map(labelOf).join(", ") || "matching spends"}`;
  const rate = describeMultiplier(rule) ?? `${rule.rate} pts / ₹${rule.unitAmount}`;
  const scope = [
    rule.categories.length > 0 ? rule.categories.map(labelOf).join(", ") : null,
    rule.merchantPatterns.length > 0 ? `at ${rule.merchantPatterns.join(" / ")}` : null,
    rule.channel !== "ANY" ? (rule.channel === "ONLINE" ? "online" : "in store") : null,
  ].filter(Boolean);
  const period = { MONTH: "a month", STATEMENT: "a statement", QUARTER: "a quarter", YEAR: "a year" }[rule.capPeriod] ?? "";
  const cap =
    rule.capType === "NONE" || rule.capValue == null
      ? null
      : `cap ${rule.capType === "POINTS" ? `${formatPoints(rule.capValue)} pts` : formatINR(rule.capValue)} ${period}` +
        (rule.capAppliesTo === "BONUS" ? ", bonus only" : rule.capOverflow === "NOTHING" ? ", nothing past it" : "") +
        (rule.capGroup ? `, shared: ${rule.capGroup}` : "");
  return [`${rate}${scope.length ? ` on ${scope.join(" ")}` : " on everything"}`, cap].filter(Boolean).join(" · ");
}

export default function SharedPage() {
  const meta = useMeta();
  const [received, setReceived] = useState<ReceivedShare[] | null>(null);
  const [mine, setMine] = useState<MyShare[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sharing, setSharing] = useState<{ kind: "TEMPLATE" | "RULE"; sourceId: string; title: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, m, c] = await Promise.all([
        api.get<{ shares: ReceivedShare[] }>("/shares/received"),
        api.get<{ shares: MyShare[] }>("/shares/mine"),
        api.get<{ cards: Card[] }>("/cards"),
      ]);
      setReceived(r.shares);
      setMine(m.shares);
      setCards(c.cards);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load what has been shared");
      setReceived([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act(fn: () => Promise<unknown>, done: string) {
    setError(null);
    try {
      await fn();
      setNotice(done);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work");
    }
  }

  const labelOf = (key: string) => meta?.categories.find((c) => c.key === key)?.label ?? key;

  return (
    <>
      <PageHeader
        title="Shared with friends"
        subtitle="Card templates and earn rules people have shared with you, and what you have shared. Only how a card earns is ever shared — never transactions, balances or card numbers."
      />
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <p className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{notice}</p>
      )}

      <Panel title="Shared with you" className="mb-6">
        {received === null ? (
          <Spinner />
        ) : received.length === 0 ? (
          <EmptyState
            title="Nothing shared with you yet"
            hint="When a friend shares a card template or a rule with your number, it appears here for you to add to your own cards."
          />
        ) : (
          <ul className="space-y-4">
            {received.map((s) =>
              s.kind === "TEMPLATE" ? (
                <TemplateShare key={s.id} share={s} labelOf={labelOf} act={act} />
              ) : (
                <RuleShare key={s.id} share={s} cards={cards} labelOf={labelOf} act={act} />
              ),
            )}
          </ul>
        )}
      </Panel>

      <Panel
        title="What you have shared"
        subtitle="Share a template from Settings → Templates, or a rule from Share beside it on a card. Editing it offers the change to everyone you shared it with."
      >
        {mine.length === 0 ? (
          <EmptyState title="You haven't shared anything" />
        ) : (
          <ul className="divide-y divide-ink-100">
            {mine.map((s) => (
              <li key={s.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {s.title} <Badge>{s.kind === "RULE" ? "Rule" : "Card template"}</Badge>
                      {s.version > 1 && <span className="ml-2 text-xs text-ink-500">version {s.version}</span>}
                    </p>
                    {s.note && <p className="text-xs text-ink-500">“{s.note}”</p>}
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {s.recipients.length === 0 && <span className="text-xs text-ink-500">No one yet</span>}
                      {s.recipients.map((r) => (
                        <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">
                          {r.label}
                          <button
                            className="text-ink-400 hover:text-red-700"
                            aria-label={`Stop sharing with ${r.label}`}
                            onClick={() => void act(() => api.del(`/shares/${s.id}/recipients/${r.id}`), `Stopped sharing with ${r.label}.`)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-3">
                    <button className="text-xs text-accent-700 underline" onClick={() => setSharing({ kind: s.kind, sourceId: s.sourceId, title: s.title })}>
                      Share with more people
                    </button>
                    <button
                      className="text-xs text-red-700 underline"
                      onClick={() => {
                        if (confirm(`Stop sharing "${s.title}" with everyone? Copies they have taken stay theirs.`)) {
                          void act(() => api.del(`/shares/${s.id}`), `Stopped sharing "${s.title}".`);
                        }
                      }}
                    >
                      Stop sharing
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <ShareModal
        target={sharing}
        onClose={() => setSharing(null)}
        onShared={(m) => { setSharing(null); setNotice(m); void load(); }}
      />
    </>
  );
}

type Act = (fn: () => Promise<unknown>, done: string) => Promise<void>;

function ShareHeading({ share, kindLabel }: { share: ReceivedShare; kindLabel: string }) {
  return (
    <>
      <p className="text-sm font-medium text-ink-900">
        {share.title} <Badge>{kindLabel}</Badge>
        {share.updateAvailable && <Badge tone="info">Update available</Badge>}
      </p>
      <p className="text-xs text-ink-500">
        From {share.from} · {formatDate(share.updatedAt)}
        {share.version > 1 ? ` · version ${share.version}` : ""}
      </p>
      {share.note && <p className="mt-1 text-sm text-ink-700">“{share.note}”</p>}
    </>
  );
}

function TemplateShare({ share, labelOf, act }: { share: ReceivedShare; labelOf: (k: string) => string; act: Act }) {
  const spec = share.spec as TemplateSpec;
  const [open, setOpen] = useState(false);
  const taken = share.copies.templates.length > 0;
  return (
    <li className="rounded-lg border border-ink-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <ShareHeading share={share} kindLabel="Card template" />
          <p className="mt-1 text-xs text-ink-600">
            {spec.issuer} · {spec.annualFee === 0 ? "No annual fee" : `${formatINR(spec.annualFee)} a year`} · {spec.rules.length} rules
            {spec.milestones.length > 0 ? ` · ${spec.milestones.length} milestones` : ""} · {spec.currency.name}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!taken ? (
            <button className="btn-primary text-sm" onClick={() => void act(() => api.post(`/shares/${share.id}/take`), `Added "${spec.name}" to your templates. Add a card from it on Cards → Add from catalogue.`)}>
              Add to my templates
            </button>
          ) : share.updateAvailable ? (
            <button className="btn-primary text-sm" onClick={() => void act(() => api.post(`/shares/${share.id}/update-copies`), `Your copy of "${spec.name}" is up to date. Cards made from it change only if you update them from Settings → Templates.`)}>
              Update my copy
            </button>
          ) : (
            <span className="text-xs text-emerald-700">✓ In your templates</span>
          )}
          <button className="btn-ghost text-sm" onClick={() => setOpen((o) => !o)}>{open ? "Hide rules" : "See rules"}</button>
          <button className="btn-ghost text-sm" onClick={() => void act(() => api.post(`/shares/${share.id}/dismiss`), "Hidden.")}>Hide</button>
        </div>
      </div>
      {open && (
        <ul className="mt-3 space-y-1 border-t border-ink-100 pt-3 text-xs text-ink-600">
          {spec.rules.map((r, i) => (
            <li key={i}>
              <span className="font-medium text-ink-800">{r.name}</span> — {describeRule(r, labelOf)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function RuleShare({
  share,
  cards,
  labelOf,
  act,
}: {
  share: ReceivedShare;
  cards: Card[];
  labelOf: (k: string) => string;
  act: Act;
}) {
  const { rule, card } = share.spec as { rule: TemplateRuleSpec; card: { name: string; issuer: string } };
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const onCards = new Set(share.copies.rules.map((r) => r.cardId));
  const available = cards.filter((c) => c.isActive && !onCards.has(c.id));

  return (
    <li className="rounded-lg border border-ink-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <ShareHeading share={share} kindLabel="Rule" />
          <p className="mt-1 text-xs text-ink-600">
            {describeRule(rule, labelOf)}
            <span className="text-ink-400"> · from their {card.name}</span>
          </p>
          {share.copies.rules.length > 0 && (
            <p className="mt-1 text-xs text-emerald-700">
              ✓ On {share.copies.rules.map((r) => r.cardName).join(", ")}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {share.updateAvailable && (
            <button className="btn-primary text-sm" onClick={() => void act(() => api.post(`/shares/${share.id}/update-copies`), "Your copies of the rule are up to date.")}>
              Update my copies
            </button>
          )}
          {!picking && (
            <button className="btn-secondary text-sm" disabled={available.length === 0} onClick={() => { setChosen([]); setPicking(true); }}>
              Add to my cards…
            </button>
          )}
          <button className="btn-ghost text-sm" onClick={() => void act(() => api.post(`/shares/${share.id}/dismiss`), "Hidden.")}>Hide</button>
        </div>
      </div>

      {picking && (
        <div className="mt-3 border-t border-ink-100 pt-3">
          {available.length === 0 ? (
            <p className="text-sm text-ink-600">
              It is already on every card you have. <Link to="/cards" className="text-accent-700 underline">Add a card</Link> to use it on another.
            </p>
          ) : (
            <>
              <p className="mb-2 text-sm text-ink-700">Which of your cards should it go on?</p>
              <div className="flex flex-wrap gap-2">
                {available.map((c) => {
                  const on = chosen.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      aria-pressed={on}
                      className={`chip-action ring-1 ${on ? "bg-accent-50 text-accent-800 ring-accent-200" : "bg-white text-ink-600 ring-ink-200 hover:bg-ink-50"}`}
                      onClick={() => setChosen(on ? chosen.filter((x) => x !== c.id) : [...chosen, c.id])}
                    >
                      <span className="mr-1.5 inline-block h-2 w-2 rounded-sm align-middle" style={{ background: c.color }} aria-hidden />
                      {c.name}
                    </button>
                  );
                })}
              </div>
              {rule.multiplier != null && (
                <p className="mt-2 text-xs text-ink-500">
                  It is set as a multiple, so on each card it earns that multiple of the card's own base rate.
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  className="btn-primary text-sm"
                  disabled={chosen.length === 0}
                  onClick={() =>
                    void act(
                      () => api.post(`/shares/${share.id}/add-to-cards`, { cardIds: chosen }),
                      `Added "${rule.name}" to ${chosen.length} card${chosen.length === 1 ? "" : "s"}. Recalculate points on Transactions to apply it to spends already logged.`,
                    ).then(() => setPicking(false))
                  }
                >
                  Add to {chosen.length || ""} card{chosen.length === 1 ? "" : "s"}
                </button>
                <button className="btn-secondary text-sm" onClick={() => setPicking(false)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}
